import React, { useEffect, useState, useMemo } from 'react';
import {
  Paper,
  Typography,
  Stack,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Switch,
  IconButton,
  Chip,
  Tooltip,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Alert,
  CircularProgress,
  FormHelperText,
  Collapse,
  InputAdornment,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Badge,
  ToggleButton,
  ToggleButtonGroup,
  Slider,
  Radio,
  RadioGroup,
} from '@mui/material';

import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar, PickersDay } from '@mui/x-date-pickers';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import InfoIcon from '@mui/icons-material/Info';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import AllInboxIcon from '@mui/icons-material/AllInbox';
import HotelIcon from '@mui/icons-material/Hotel';
import VillaIcon from '@mui/icons-material/Villa';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import SearchIcon from '@mui/icons-material/Search';
import DateRangeIcon from '@mui/icons-material/DateRange';
import TuneIcon from '@mui/icons-material/Tune';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const BLOCK_TYPES = ['Mantenimiento', 'Reparación', 'Evento', 'Agotada', 'Otro'];
const SCOPES = [
  { value: 'specific', label: 'Habitación específica', icon: <MeetingRoomIcon /> },
  { value: 'casaHotel', label: 'Todas Casa Hotel', icon: <HotelIcon /> },
  { value: 'boutique', label: 'Todas Boutique', icon: <VillaIcon /> },
  { value: 'all', label: 'Todas las habitaciones', icon: <AllInboxIcon /> },
];

// Función optimizada para manejar fechas sin plugins pesados
function parseDate(dateStr) {
  if (!dateStr) return null;
  
  // Si ya es un objeto dayjs, retornarlo
  if (dayjs.isDayjs(dateStr)) {
    return dateStr.startOf('day');
  }
  
  // Si es un objeto Date, convertirlo
  if (dateStr instanceof Date) {
    return dayjs(dateStr).startOf('day');
  }
  
  // Si es string, intentar parsearlo
  // Primero intentar con formato ISO
  let date = dayjs(dateStr);
  
  // Si no es válido, intentar otros formatos comunes
  if (!date.isValid()) {
    // Intentar parsear como YYYY-MM-DD
    if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      date = dayjs(dateStr, 'YYYY-MM-DD');
    }
    // Intentar parsear como YYYY-MM-DDTHH:mm:ss
    else if (typeof dateStr === 'string' && dateStr.includes('T')) {
      // Extraer solo la parte de la fecha
      const datePart = dateStr.split('T')[0];
      date = dayjs(datePart, 'YYYY-MM-DD');
    }
  }
  
  return date.isValid() ? date.startOf('day') : null;
}

// Función para mostrar fechas
function formatDateForDisplay(date) {
  const parsed = parseDate(date);
  return parsed ? parsed.format('DD/MM/YYYY') : '';
}

// Función para formato ISO para inputs
function toISODateOnly(date) {
  const parsed = parseDate(date);
  return parsed ? parsed.format('YYYY-MM-DD') : '';
}

// Función para enviar a API - manejar problema de zona horaria
function prepareDateForAPI(dateStr) {
  const date = parseDate(dateStr);
  if (!date) return '';
  
  // Agregar la hora del mediodía para evitar problemas de zona horaria
  return date.hour(12).format('YYYY-MM-DDTHH:mm:ss[Z]');
}

export function ControlDisponibilidad() {
  const [rooms, setRooms] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [selectedDate, setSelectedDate] = useState(dayjs());

  const [form, setForm] = useState({
    scope: 'specific',
    roomId: '',
    startDate: dayjs().format('YYYY-MM-DD'),
    endDate: dayjs().add(1, 'day').format('YYYY-MM-DD'),
    blockType: 'Mantenimiento',
    reason: '',
    blockAll: false,
    quantityBlocked: 1,
  });

  const [range, setRange] = useState([null, null]);

  // Estados para filtros
  const [filters, setFilters] = useState({
    search: '',
    scope: [],
    blockType: [],
    lugar: [],
    status: ['active'],
    dateRange: {
      start: null,
      end: null,
    },
    quantityRange: [0, 100],
  });

  const [showFilters, setShowFilters] = useState(false);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);

  // Memoizar cálculos pesados
  const parsedBlocks = useMemo(() => {
    return blocks.map(block => ({
      ...block,
      parsedStartDate: parseDate(block.startDate),
      parsedEndDate: parseDate(block.endDate)
    }));
  }, [blocks]);

  // Cargar habitaciones y bloqueos
  useEffect(() => {
    fetchRooms();
    fetchBlocks();
  }, []);

  // Calcular cantidad de filtros activos
  useEffect(() => {
    let count = 0;
    if (filters.search.trim()) count++;
    if (filters.scope.length > 0) count++;
    if (filters.blockType.length > 0) count++;
    if (filters.lugar.length > 0) count++;
    if (filters.status.length !== 1 || filters.status[0] !== 'active') count++;
    if (filters.dateRange.start || filters.dateRange.end) count++;
    if (filters.quantityRange[0] > 0 || filters.quantityRange[1] < 100) count++;
    setActiveFiltersCount(count);
  }, [filters]);

  async function fetchRooms() {
    try {
      const response = await fetch(`${API_URL}/api/rooms/admin`);
      if (!response.ok) throw new Error('Error al cargar habitaciones');
      const data = await response.json();
      setRooms(data);
    } catch (err) {
      console.error('Error al cargar habitaciones:', err);
      setError('No se pudieron cargar las habitaciones');
    }
  }

  async function fetchBlocks() {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/room-blocks`);
      if (!response.ok) throw new Error('Error al cargar bloqueos');
      const data = await response.json();
      setBlocks(data);
    } catch (err) {
      console.error('Error al cargar bloqueos:', err);
      setError('No se pudieron cargar los bloqueos');
    } finally {
      setLoading(false);
    }
  }

  // Calcular bloqueos para cada día del mes actual (OPTIMIZADO)
  const monthBlocks = useMemo(() => {
    const result = {};
    const startOfMonth = currentMonth.startOf('month');
    const endOfMonth = currentMonth.endOf('month');
    
    let currentDay = startOfMonth;
    while (currentDay.isBefore(endOfMonth) || currentDay.isSame(endOfMonth, 'day')) {
      const dateStr = currentDay.format('YYYY-MM-DD');
      result[dateStr] = [];
      
      parsedBlocks.forEach(block => {
        if (!block.active) return;
        
        const blockStart = block.parsedStartDate;
        const blockEnd = block.parsedEndDate;
        
        if (!blockStart || !blockEnd) return;
        
        // Verificar si el día actual está dentro del rango del bloqueo
        const isInRange = currentDay.isSame(blockStart, 'day') || 
                         currentDay.isSame(blockEnd, 'day') || 
                         (currentDay.isAfter(blockStart, 'day') && currentDay.isBefore(blockEnd, 'day'));
        
        if (isInRange) {
          // Determinar qué habitaciones están afectadas
          let affectedRooms = [];
          if (block.scope === 'specific' && block.roomId) {
            affectedRooms.push({
              id: block.roomId._id,
              name: block.roomId.name,
              type: block.roomId.type,
              lugar: block.roomId.lugar,
            });
          } else if (block.scope === 'casaHotel' || block.scope === 'boutique' || block.scope === 'all') {
            // Para scopes no específicos, usar affectedRooms
            if (block.affectedRooms && block.affectedRooms.length > 0) {
              block.affectedRooms.forEach(room => {
                affectedRooms.push({
                  id: room._id,
                  name: room.name,
                  type: room.type,
                  lugar: room.lugar,
                });
              });
            }
          }
          
          // Agregar información del bloqueo para cada habitación afectada
          affectedRooms.forEach(room => {
            result[dateStr].push({
              roomId: room.id,
              roomName: room.name,
              roomType: room.type,
              roomLugar: room.lugar,
              blockId: block._id,
              scope: block.scope,
              blockType: block.blockType,
              reason: block.reason,
              blockAll: block.blockAll,
              quantity: block.blockAll ? 'Todas' : block.quantityBlocked,
              totalRooms: room.totalUnits || 0,
            });
          });
        }
      });
      
      currentDay = currentDay.add(1, 'day');
    }
    
    return result;
  }, [currentMonth, parsedBlocks]);

  // Estadísticas del mes (OPTIMIZADO)
  const monthStats = useMemo(() => {
    const stats = {
      totalDaysWithBlocks: 0,
      totalBlocks: 0,
      roomsAffected: new Set(),
      mostAffectedRoom: null,
      maxBlocksInDay: 0,
      busiestDay: null,
      scopes: {
        specific: 0,
        casaHotel: 0,
        boutique: 0,
        all: 0,
      }
    };
    
    Object.entries(monthBlocks).forEach(([date, blocksForDate]) => {
      if (blocksForDate.length > 0) {
        stats.totalDaysWithBlocks++;
        stats.totalBlocks += blocksForDate.length;
        
        if (blocksForDate.length > stats.maxBlocksInDay) {
          stats.maxBlocksInDay = blocksForDate.length;
          stats.busiestDay = date;
        }
        
        blocksForDate.forEach(block => {
          stats.roomsAffected.add(block.roomId);
        });
      }
    });
    
    // Contar scopes
    blocks.forEach(block => {
      if (block.active) {
        stats.scopes[block.scope] = (stats.scopes[block.scope] || 0) + 1;
      }
    });
    
    const roomBlockCount = {};
    Object.values(monthBlocks).forEach(blocksForDate => {
      blocksForDate.forEach(block => {
        roomBlockCount[block.roomId] = (roomBlockCount[block.roomId] || 0) + 1;
      });
    });
    
    let maxCount = 0;
    let mostAffectedId = null;
    Object.entries(roomBlockCount).forEach(([roomId, count]) => {
      if (count > maxCount) {
        maxCount = count;
        mostAffectedId = roomId;
      }
    });
    
    if (mostAffectedId) {
      const room = rooms.find(r => r._id === mostAffectedId);
      stats.mostAffectedRoom = room ? `${room.name} (${room.lugar})` : 'Desconocida';
    }
    
    return stats;
  }, [monthBlocks, rooms, blocks]);

  // Filtrar bloqueos (OPTIMIZADO)
  const filteredBlocks = useMemo(() => {
    return parsedBlocks.filter(block => {
      // Filtro de búsqueda
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const roomName = (block.roomId?.name || '').toLowerCase();
        const reason = (block.reason || '').toLowerCase();
        const blockType = (block.blockType || '').toLowerCase();
        
        if (!roomName.includes(searchTerm) && 
            !reason.includes(searchTerm) && 
            !blockType.includes(searchTerm)) {
          return false;
        }
      }
      
      // Filtro por scope
      if (filters.scope.length > 0 && !filters.scope.includes(block.scope)) {
        return false;
      }
      
      // Filtro por tipo de bloqueo
      if (filters.blockType.length > 0 && !filters.blockType.includes(block.blockType)) {
        return false;
      }
      
      // Filtro por lugar
      if (filters.lugar.length > 0) {
        if (block.scope === 'specific') {
          const roomLugar = block.roomId?.lugar || '';
          if (!filters.lugar.includes(roomLugar)) {
            return false;
          }
        } else if (block.scope === 'casaHotel') {
          if (!filters.lugar.includes('casaHotel')) return false;
        } else if (block.scope === 'boutique') {
          if (!filters.lugar.includes('boutique')) return false;
        } else if (block.scope === 'all') {
          // Si es 'all', pasa si hay al menos un lugar seleccionado
          if (filters.lugar.length < 2) return false;
        }
      }
      
      // Filtro por estado
      if (filters.status.length > 0) {
        const isActive = block.active ? 'active' : 'inactive';
        if (!filters.status.includes(isActive)) {
          return false;
        }
      }
      
      // Filtro por rango de fechas
      if (filters.dateRange.start || filters.dateRange.end) {
        const blockStart = block.parsedStartDate;
        const blockEnd = block.parsedEndDate;
        
        if (!blockStart || !blockEnd) return false;
        
        if (filters.dateRange.start) {
          const filterStart = parseDate(filters.dateRange.start);
          if (filterStart && blockEnd.isBefore(filterStart)) {
            return false;
          }
        }
        
        if (filters.dateRange.end) {
          const filterEnd = parseDate(filters.dateRange.end);
          if (filterEnd && blockStart.isAfter(filterEnd)) {
            return false;
          }
        }
      }
      
      // Filtro por cantidad
      if (block.blockAll) {
        // Si blockAll es true, se considera como cantidad máxima
        if (filters.quantityRange[1] < 100) return false;
      } else {
        const quantity = block.quantityBlocked || 0;
        if (quantity < filters.quantityRange[0] || quantity > filters.quantityRange[1]) {
          return false;
        }
      }
      
      return true;
    });
  }, [parsedBlocks, filters]);

  // Función para limpiar todos los filtros
  const clearAllFilters = () => {
    setFilters({
      search: '',
      scope: [],
      blockType: [],
      lugar: [],
      status: ['active'],
      dateRange: {
        start: null,
        end: null,
      },
      quantityRange: [0, 100],
    });
  };

  function openCreate() {
    setEditing(null);
    const today = dayjs();
    const tomorrow = today.add(1, 'day');
    
    setForm({
      scope: 'specific',
      roomId: rooms[0]?._id || '',
      startDate: today.format('YYYY-MM-DD'),
      endDate: tomorrow.format('YYYY-MM-DD'),
      blockType: 'Mantenimiento',
      reason: '',
      blockAll: false,
      quantityBlocked: 1,
    });
    
    setRange([today, tomorrow]);
    setOpenDialog(true);
  }

  function openEdit(block) {
    setEditing(block);
    const start = parseDate(block.startDate) || dayjs();
    const end = parseDate(block.endDate) || dayjs().add(1, 'day');
    
    setForm({
      scope: block.scope || 'specific',
      roomId: block.roomId?._id || block.roomId || '',
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
      blockType: block.blockType,
      reason: block.reason || '',
      blockAll: block.scope === 'specific' ? !!block.blockAll : true,
      quantityBlocked: block.quantityBlocked || 1,
    });
    
    setRange([start, end]);
    setOpenDialog(true);
  }

  async function handleDelete(id) {
    if (!window.confirm('¿Eliminar bloqueo?')) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/room-blocks/${id}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) throw new Error('Error al eliminar bloqueo');
      
      setSuccess('Bloqueo eliminado exitosamente');
      await fetchBlocks();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error al eliminar bloqueo:', err);
      setError('No se pudo eliminar el bloqueo');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    if (form.scope === 'specific' && !form.roomId) {
      setError('Seleccione una habitación');
      return;
    }

    const blockData = {
      scope: form.scope,
      roomId: form.scope === 'specific' ? form.roomId : undefined,
      startDate: prepareDateForAPI(form.startDate), // Usar función optimizada
      endDate: prepareDateForAPI(form.endDate),     // Usar función optimizada
      blockType: form.blockType,
      reason: form.reason,
      blockAll: form.scope === 'specific' ? form.blockAll : true,
      quantityBlocked: form.scope === 'specific' && !form.blockAll ? Number(form.quantityBlocked) : null,
    };

    setLoading(true);
    setError(null);
    
    try {
      let response;
      
      if (editing) {
        response = await fetch(`${API_URL}/api/room-blocks/${editing._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(blockData),
        });
      } else {
        response = await fetch(`${API_URL}/api/room-blocks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(blockData),
        });
      }

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Error al guardar bloqueo');
      }

      setSuccess(editing ? 'Bloqueo actualizado exitosamente' : 'Bloqueo creado exitosamente');
      setOpenDialog(false);
      await fetchBlocks();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error al guardar bloqueo:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // Memoizar fechas bloqueadas para mejor rendimiento
  const blockedDatesCache = useMemo(() => {
    const cache = {};
    rooms.forEach(room => {
      cache[room._id] = getBlockedDatesForRoom(room._id);
    });
    return cache;
  }, [rooms, parsedBlocks]);

  function getBlockedDatesForRoom(roomId) {
    if (!roomId) return [];
    
    const room = rooms.find(r => r._id === roomId);
    if (!room) return [];
    
    const blockedDates = new Set();
    parsedBlocks.forEach(block => {
      if (!block.active) return;
      
      // Verificar si este bloqueo afecta a la habitación
      let affectsRoom = false;
      
      if (block.scope === 'all') {
        affectsRoom = true;
      } else if (block.scope === 'casaHotel') {
        affectsRoom = room.lugar === 'casaHotel';
      } else if (block.scope === 'boutique') {
        affectsRoom = room.lugar === 'boutique';
      } else if (block.scope === 'specific') {
        affectsRoom = (block.roomId?._id === roomId || block.roomId === roomId);
      }
      
      if (affectsRoom) {
        const blockStart = block.parsedStartDate;
        const blockEnd = block.parsedEndDate;
        
        if (!blockStart || !blockEnd) return;
        
        let current = blockStart;
        
        while (current.isBefore(blockEnd) || current.isSame(blockEnd, 'day')) {
          blockedDates.add(current.format('YYYY-MM-DD'));
          current = current.add(1, 'day');
        }
      }
    });
    
    return Array.from(blockedDates);
  }

  function isDayBlocked(day, roomId) {
    if (!roomId) return false;
    const blockedDates = blockedDatesCache[roomId] || getBlockedDatesForRoom(roomId);
    return blockedDates.includes(day.format('YYYY-MM-DD'));
  }

  function isInRange(day, start, end) {
    if (!start || !end) return false;
    const dayNormalized = day.startOf('day');
    const startNormalized = parseDate(start)?.startOf('day');
    const endNormalized = parseDate(end)?.startOf('day');
    
    if (!startNormalized || !endNormalized) return false;
    
    return (dayNormalized.isSame(startNormalized) || dayNormalized.isAfter(startNormalized)) &&
           (dayNormalized.isSame(endNormalized) || dayNormalized.isBefore(endNormalized));
  }

  function isStartOrEnd(day, start, end) {
    if (!start || !end) return false;
    const dayNormalized = day.startOf('day');
    const startNormalized = parseDate(start)?.startOf('day');
    const endNormalized = parseDate(end)?.startOf('day');
    
    if (!startNormalized || !endNormalized) return false;
    
    return dayNormalized.isSame(startNormalized) || dayNormalized.isSame(endNormalized);
  }

  function handleRangeSelect(day) {
    // Si es scope 'specific', necesitamos roomId
    if (form.scope === 'specific' && !form.roomId) {
      alert('Por favor, seleccione una habitación primero');
      return;
    }

    const dayNormalized = day.startOf('day');

    // Verificar si la fecha está bloqueada (solo para scope 'specific')
    if (form.scope === 'specific' && isDayBlocked(dayNormalized, form.roomId)) {
      alert('Esta fecha está bloqueada. Seleccione otra fecha.');
      return;
    }

    if (!range[0] || (range[0] && range[1])) {
      setRange([dayNormalized, null]);
      setForm(prev => ({
        ...prev,
        startDate: dayNormalized.format('YYYY-MM-DD'),
        endDate: '',
      }));
    } else {
      const start = range[0]?.startOf('day') || dayNormalized;
      
      if (dayNormalized.isBefore(start)) {
        alert('La fecha fin debe ser posterior a la fecha inicio');
        return;
      }

      // Para scope 'specific', verificar fechas bloqueadas en el rango
      if (form.scope === 'specific' && form.roomId) {
        let current = start.add(1, 'day');
        const blockedDates = blockedDatesCache[form.roomId] || getBlockedDatesForRoom(form.roomId);
        let hasBlocked = false;
        
        while (current.isBefore(dayNormalized)) {
          if (blockedDates.includes(current.format('YYYY-MM-DD'))) {
            hasBlocked = true;
            break;
          }
          current = current.add(1, 'day');
        }

        if (hasBlocked) {
          alert('Hay fechas bloqueadas dentro del rango seleccionado.');
          return;
        }
      }

      setRange([start, dayNormalized]);
      setForm(prev => ({
        ...prev,
        startDate: start.format('YYYY-MM-DD'),
        endDate: dayNormalized.format('YYYY-MM-DD'),
      }));
    }
  }

  const goToPreviousMonth = () => setCurrentMonth(currentMonth.subtract(1, 'month'));
  const goToNextMonth = () => setCurrentMonth(currentMonth.add(1, 'month'));
  const goToToday = () => {
    const today = dayjs();
    setCurrentMonth(today);
    setSelectedDate(today);
  };

  const getBlockTypeColor = (type) => {
    switch(type) {
      case 'Mantenimiento': return 'error';
      case 'Reparación': return 'warning';
      case 'Evento': return 'info';
      case 'Agotada': return 'default';
      case 'Otro': return 'default';
      default: return 'primary';
    }
  };

  const getScopeColor = (scope) => {
    switch(scope) {
      case 'specific': return 'primary';
      case 'casaHotel': return 'success';
      case 'boutique': return 'warning';
      case 'all': return 'error';
      default: return 'default';
    }
  };

  const getScopeIcon = (scope) => {
    const scopeObj = SCOPES.find(s => s.value === scope);
    return scopeObj ? scopeObj.icon : <MeetingRoomIcon />;
  };

  // Calcular estadísticas de habitaciones por lugar
  const roomStats = useMemo(() => {
    const stats = {
      total: rooms.length,
      casaHotel: rooms.filter(r => r.lugar === 'casaHotel').length,
      boutique: rooms.filter(r => r.lugar === 'boutique').length,
    };
    return stats;
  }, [rooms]);

  // Componente de filtros avanzados
  const AdvancedFilters = () => (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: 'primary.light' }}>
            <TuneIcon />
          </Avatar>
        }
        title={
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Filtros Avanzados</Typography>
            <Badge badgeContent={activeFiltersCount} color="primary">
              <FilterAltIcon color="action" />
            </Badge>
          </Stack>
        }
        subheader="Aplica múltiples criterios de búsqueda"
        action={
          <Stack direction="row" spacing={1}>
            <IconButton 
              size="small" 
              onClick={clearAllFilters}
              disabled={activeFiltersCount === 0}
            >
              <FilterAltOffIcon />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>
        }
      />
      <Collapse in={showFilters}>
        <CardContent>
          <Grid container spacing={2}>
            {/* Rango de fechas */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <Typography variant="subtitle2" gutterBottom>
                  Rango de fechas del bloqueo
                </Typography>
                <Stack direction="row" spacing={1}>
                  <TextField
                    type="date"
                    label="Desde"
                    value={filters.dateRange.start ? toISODateOnly(filters.dateRange.start) : ''}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      dateRange: {
                        ...prev.dateRange,
                        start: e.target.value ? dayjs(e.target.value) : null
                      }
                    }))}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    type="date"
                    label="Hasta"
                    value={filters.dateRange.end ? toISODateOnly(filters.dateRange.end) : ''}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      dateRange: {
                        ...prev.dateRange,
                        end: e.target.value ? dayjs(e.target.value) : null
                      }
                    }))}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                  />
                </Stack>
              </FormControl>
            </Grid>

            {/* Rango de cantidad */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <Typography variant="subtitle2" gutterBottom>
                  Cantidad bloqueada: {filters.quantityRange[0]} - {filters.quantityRange[1]}
                </Typography>
                <Slider
                  value={filters.quantityRange}
                  onChange={(_, newValue) => setFilters(prev => ({
                    ...prev,
                    quantityRange: newValue
                  }))}
                  valueLabelDisplay="auto"
                  min={0}
                  max={100}
                  step={1}
                />
              </FormControl>
            </Grid>

            {/* Estado */}
            <Grid item xs={12} md={4}>
              <FormControl component="fieldset" fullWidth>
                <Typography variant="subtitle2" gutterBottom>
                  Estado
                </Typography>
                <RadioGroup
                  value={filters.status[0]}
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    status: [e.target.value]
                  }))}
                >
                  <Stack direction="row" spacing={1}>
                    <FormControlLabel 
                      value="active" 
                      control={<Radio size="small" />} 
                      label="Activo" 
                    />
                    <FormControlLabel 
                      value="inactive" 
                      control={<Radio size="small" />} 
                      label="Inactivo" 
                    />
                    <FormControlLabel 
                      value="all" 
                      control={<Radio size="small" />} 
                      label="Todos" 
                    />
                  </Stack>
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* Scope */}
            <Grid item xs={12} md={4}>
              <FormControl component="fieldset" fullWidth>
                <Typography variant="subtitle2" gutterBottom>
                  Alcance
                </Typography>
                <FormGroup>
                  {SCOPES.map((scope) => (
                    <FormControlLabel
                      key={scope.value}
                      control={
                        <Checkbox
                          size="small"
                          checked={filters.scope.includes(scope.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFilters(prev => ({
                                ...prev,
                                scope: [...prev.scope, scope.value]
                              }));
                            } else {
                              setFilters(prev => ({
                                ...prev,
                                scope: prev.scope.filter(s => s !== scope.value)
                              }));
                            }
                          }}
                        />
                      }
                      label={scope.label}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Grid>

            {/* Lugar */}
            <Grid item xs={12} md={4}>
              <FormControl component="fieldset" fullWidth>
                <Typography variant="subtitle2" gutterBottom>
                  Lugar
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={filters.lugar.includes('casaHotel')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters(prev => ({
                              ...prev,
                              lugar: [...prev.lugar, 'casaHotel']
                            }));
                          } else {
                            setFilters(prev => ({
                              ...prev,
                              lugar: prev.lugar.filter(l => l !== 'casaHotel')
                            }));
                          }
                        }}
                      />
                    }
                    label="Casa Hotel"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={filters.lugar.includes('boutique')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters(prev => ({
                              ...prev,
                              lugar: [...prev.lugar, 'boutique']
                            }));
                          } else {
                            setFilters(prev => ({
                              ...prev,
                              lugar: prev.lugar.filter(l => l !== 'boutique')
                            }));
                          }
                        }}
                      />
                    }
                    label="Boutique"
                  />
                </FormGroup>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Collapse>
    </Card>
  );

  // Chips de filtros activos
  const ActiveFilterChips = () => (
    <Stack 
      direction="row" 
      spacing={1} 
      sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
      alignItems="center"
    >
      <Typography variant="caption" color="textSecondary">
        Filtros activos:
      </Typography>
      
      {/* Search filter */}
      {filters.search && (
        <Chip
          label={`Buscar: "${filters.search}"`}
          size="small"
          onDelete={() => setFilters(prev => ({ ...prev, search: '' }))}
          deleteIcon={<ClearIcon />}
        />
      )}
      
      {/* Scope filters */}
      {filters.scope.map(scope => (
        <Chip
          key={scope}
          label={`Alcance: ${SCOPES.find(s => s.value === scope)?.label}`}
          size="small"
          color={getScopeColor(scope)}
          onDelete={() => setFilters(prev => ({
            ...prev,
            scope: prev.scope.filter(s => s !== scope)
          }))}
          deleteIcon={<ClearIcon />}
        />
      ))}
      
      {/* Block type filters */}
      {filters.blockType.map(type => (
        <Chip
          key={type}
          label={`Tipo: ${type}`}
          size="small"
          color={getBlockTypeColor(type)}
          onDelete={() => setFilters(prev => ({
            ...prev,
            blockType: prev.blockType.filter(t => t !== type)
          }))}
          deleteIcon={<ClearIcon />}
        />
      ))}
      
      {/* Lugar filters */}
      {filters.lugar.map(lugar => (
        <Chip
          key={lugar}
          label={`Lugar: ${lugar === 'casaHotel' ? 'Casa Hotel' : 'Boutique'}`}
          size="small"
          color="info"
          onDelete={() => setFilters(prev => ({
            ...prev,
            lugar: prev.lugar.filter(l => l !== lugar)
          }))}
          deleteIcon={<ClearIcon />}
        />
      ))}
      
      {/* Date range filter */}
      {(filters.dateRange.start || filters.dateRange.end) && (
        <Chip
          label={`Fechas: ${filters.dateRange.start?.format('DD/MM') || '...'} - ${filters.dateRange.end?.format('DD/MM') || '...'}`}
          size="small"
          color="warning"
          onDelete={() => setFilters(prev => ({
            ...prev,
            dateRange: { start: null, end: null }
          }))}
          deleteIcon={<ClearIcon />}
        />
      )}
      
      {/* Clear all button */}
      {activeFiltersCount > 0 && (
        <Button
          size="small"
          startIcon={<ClearIcon />}
          onClick={clearAllFilters}
          sx={{ ml: 1 }}
        >
          Limpiar todos
        </Button>
      )}
    </Stack>
  );

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <CalendarMonthIcon />
        <Typography variant="h6">Control de disponibilidad</Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Typography variant="body1" color="textSecondary" sx={{ maxWidth: '60%' }}>
          Gestiona los bloqueos de disponibilidad por habitación, lugar o tipo de bloqueo.
        </Typography>
        <Button 
          startIcon={<AddIcon />} 
          variant="contained" 
          onClick={openCreate}
          disabled={loading || rooms.length === 0}
        >
          Crear bloqueo
        </Button>
      </Box>

      {/* Filtros rápidos */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            {/* Barra de búsqueda */}
            <TextField
              fullWidth
              placeholder="Buscar por habitación, motivo o tipo..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: filters.search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setFilters(prev => ({ ...prev, search: '' }))}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              size="small"
            />

            {/* Filtros rápidos por scope */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Filtros rápidos por alcance:
              </Typography>
              <ToggleButtonGroup
                value={filters.scope}
                onChange={(_, newScopes) => setFilters(prev => ({ ...prev, scope: newScopes }))}
                aria-label="scope filters"
                size="small"
                sx={{ flexWrap: 'wrap', gap: 1 }}
              >
                {SCOPES.map((scope) => (
                  <ToggleButton
                    key={scope.value}
                    value={scope.value}
                    selected={filters.scope.includes(scope.value)}
                    sx={{
                      borderColor: 'primary.light',
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                      },
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {scope.icon}
                      <Typography variant="body2">{scope.label}</Typography>
                    </Stack>
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {/* Filtros rápidos por tipo */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Filtros rápidos por tipo:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {BLOCK_TYPES.map((type) => (
                  <Chip
                    key={type}
                    label={type}
                    clickable
                    color={filters.blockType.includes(type) ? getBlockTypeColor(type) : 'default'}
                    variant={filters.blockType.includes(type) ? 'filled' : 'outlined'}
                    onClick={() => {
                      if (filters.blockType.includes(type)) {
                        setFilters(prev => ({
                          ...prev,
                          blockType: prev.blockType.filter(t => t !== type)
                        }));
                      } else {
                        setFilters(prev => ({
                          ...prev,
                          blockType: [...prev.blockType, type]
                        }));
                      }
                    }}
                    onDelete={
                      filters.blockType.includes(type)
                        ? () => setFilters(prev => ({
                            ...prev,
                            blockType: prev.blockType.filter(t => t !== type)
                          }))
                        : undefined
                    }
                    deleteIcon={<ClearIcon />}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Filtros activos */}
      {activeFiltersCount > 0 && <ActiveFilterChips />}

      {/* Filtros avanzados */}
      <AdvancedFilters />

      {/* Calendario visual */}
      <Card sx={{ mb: 4 }}>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <CalendarMonthIcon />
            </Avatar>
          }
          title={
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="h6">Calendario de Bloqueos</Typography>
              <Chip 
                icon={<InfoIcon />} 
                label={`${currentMonth.format('MMMM YYYY')}`}
                color="primary"
                variant="outlined"
              />
            </Stack>
          }
          subheader="Vista general de todos los bloqueos por fecha"
          action={
            <Stack direction="row" spacing={1}>
              <IconButton onClick={goToPreviousMonth} title="Mes anterior">
                <ChevronLeftIcon />
              </IconButton>
              <IconButton onClick={goToToday} title="Ir a hoy">
                <TodayIcon />
              </IconButton>
              <IconButton onClick={goToNextMonth} title="Mes siguiente">
                <ChevronRightIcon />
              </IconButton>
            </Stack>
          }
        />
        <CardContent>
          {/* Estadísticas */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', p: 1 }}>
                  <Typography color="textSecondary" variant="caption">
                    Días con bloqueos
                  </Typography>
                  <Typography variant="h6">{monthStats.totalDaysWithBlocks}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', p: 1 }}>
                  <Typography color="textSecondary" variant="caption">
                    Total bloqueos
                  </Typography>
                  <Typography variant="h6">{monthStats.totalBlocks}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', p: 1 }}>
                  <Typography color="textSecondary" variant="caption">
                    Hab. afectadas
                  </Typography>
                  <Typography variant="h6">{monthStats.roomsAffected.size}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', p: 1 }}>
                  <Typography color="textSecondary" variant="caption">
                    Día más ocupado
                  </Typography>
                  <Typography variant="body2">
                    {monthStats.busiestDay ? dayjs(monthStats.busiestDay).format('DD/MM') : '-'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <LocalizationProvider 
            dateAdapter={AdapterDayjs}
            adapterLocale="es"
          >
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
              <DateCalendar
                value={selectedDate}
                onChange={(newDate) => setSelectedDate(newDate)}
                onMonthChange={(newMonth) => setCurrentMonth(newMonth)}
                reduceAnimations={true}
                slots={{
                  day: (props) => {
                    const dateStr = props.day.format('YYYY-MM-DD');
                    const dayBlocks = monthBlocks[dateStr] || [];
                    const hasBlocks = dayBlocks.length > 0;
                    
                    return (
                      <Tooltip 
                        title={
                          hasBlocks ? (
                            <Box sx={{ maxWidth: 250 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                {props.day.format('DD/MM/YYYY')}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {dayBlocks.length} bloqueo(s)
                              </Typography>
                              {dayBlocks.slice(0, 3).map((block, idx) => (
                                <Box key={idx} sx={{ py: 0.5 }}>
                                  <Typography variant="caption" fontWeight="bold">
                                    {block.roomName}
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    {block.blockType}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          ) : 'Sin bloqueos'
                        }
                        arrow
                      >
                        <Box sx={{ position: 'relative' }}>
                          <PickersDay
                            {...props}
                            sx={{
                              ...(hasBlocks && {
                                bgcolor: 'error.light',
                                color: 'error.contrastText',
                                fontWeight: 'bold',
                              }),
                            }}
                          />
                          {hasBlocks && (
                            <Box sx={{
                              position: 'absolute',
                              top: 2,
                              right: 2,
                              bgcolor: 'error.main',
                              color: 'white',
                              borderRadius: '50%',
                              width: 16,
                              height: 16,
                              fontSize: '0.6rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}>
                              {dayBlocks.length}
                            </Box>
                          )}
                        </Box>
                      </Tooltip>
                    );
                  },
                }}
              />
            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {/* Contador de resultados */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="textSecondary">
          Mostrando {filteredBlocks.length} de {blocks.length} bloqueos
          {activeFiltersCount > 0 && ` (filtrados)`}
        </Typography>
        {activeFiltersCount > 0 && (
          <Button 
            startIcon={<FilterAltOffIcon />} 
            size="small" 
            onClick={clearAllFilters}
            variant="text"
          >
            Limpiar filtros
          </Button>
        )}
      </Box>

      {/* Tabla de bloqueos */}
      {loading && !openDialog ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Alcance</TableCell>
                <TableCell>Habitación/Lugar</TableCell>
                <TableCell>Rango</TableCell>
                <TableCell>Tipo / Motivo</TableCell>
                <TableCell>Cantidad</TableCell>
                <TableCell>Activo</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBlocks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Stack spacing={2} alignItems="center">
                      <FilterListIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                      <Typography variant="body1" color="text.secondary">
                        No se encontraron bloqueos
                      </Typography>
                      {activeFiltersCount > 0 && (
                        <Button 
                          variant="outlined" 
                          onClick={clearAllFilters}
                          startIcon={<ClearIcon />}
                        >
                          Limpiar filtros
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                filteredBlocks.map(b => {
                  const room = b.roomId || { name: '—', totalUnits: 0, lugar: '—' };
                  const affectedCount = b.affectedRooms?.length || 0;
                  
                  return (
                    <TableRow key={b._id} hover>
                      <TableCell>
                        <Chip 
                          icon={getScopeIcon(b.scope)}
                          label={
                            b.scope === 'specific' ? 'Individual' :
                            b.scope === 'casaHotel' ? 'Casa Hotel' :
                            b.scope === 'boutique' ? 'Boutique' : 'Todas'
                          }
                          size="small"
                          color={getScopeColor(b.scope)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {b.scope === 'specific' ? (
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <MeetingRoomIcon fontSize="small" />
                            <Box>
                              <Typography variant="body2">{room.name}</Typography>
                              <Typography variant="caption" color="textSecondary">
                                {room.lugar}
                              </Typography>
                            </Box>
                          </Stack>
                        ) : (
                          <Typography variant="body2">
                            {b.scope === 'casaHotel' ? 'Todas Casa Hotel' :
                             b.scope === 'boutique' ? 'Todas Boutique' : 'Todas las habitaciones'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {formatDateForDisplay(b.startDate)} → {formatDateForDisplay(b.endDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Chip 
                            label={b.blockType} 
                            size="small" 
                            color={getBlockTypeColor(b.blockType)}
                          />
                          {b.reason && (
                            <Typography variant="caption" sx={{ maxWidth: 200 }} noWrap>
                              {b.reason}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {b.blockAll ? (
                          <Chip 
                            label="Todas" 
                            color="warning" 
                            size="small" 
                          />
                        ) : (
                          <Chip label={b.quantityBlocked} size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={b.active ? 'Sí' : 'No'} 
                          color={b.active ? 'success' : 'default'} 
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <IconButton 
                            size="small" 
                            onClick={() => openEdit(b)}
                            disabled={loading}
                            color="primary"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDelete(b._id)}
                            disabled={loading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog crear/editar */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Editar bloqueo' : 'Crear bloqueo'}</DialogTitle>
        <Box component="form" onSubmit={handleSubmit}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Selector de Scope */}
            <FormControl fullWidth>
              <InputLabel>Alcance del bloqueo</InputLabel>
              <Select
                value={form.scope}
                label="Alcance del bloqueo"
                onChange={e => {
                  const newScope = e.target.value;
                  // Resetear fechas cuando se cambia el scope
                  const today = dayjs();
                  const tomorrow = today.add(1, 'day');
                  setRange([today, tomorrow]);
                  setForm(prev => ({
                    ...prev,
                    scope: newScope,
                    roomId: newScope === 'specific' ? prev.roomId : '',
                    startDate: today.format('YYYY-MM-DD'),
                    endDate: tomorrow.format('YYYY-MM-DD'),
                    blockAll: newScope === 'specific' ? prev.blockAll : true,
                    quantityBlocked: newScope === 'specific' ? prev.quantityBlocked : 1,
                  }));
                }}
                disabled={loading}
              >
                {SCOPES.map(scope => (
                  <MenuItem key={scope.value} value={scope.value}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {scope.icon}
                      <Typography>{scope.label}</Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {form.scope === 'specific' && 'Bloquear una habitación específica'}
                {form.scope === 'casaHotel' && `Bloqueará todas las habitaciones de Casa Hotel (${roomStats.casaHotel})`}
                {form.scope === 'boutique' && `Bloqueará todas las habitaciones de Boutique (${roomStats.boutique})`}
                {form.scope === 'all' && `Bloqueará todas las habitaciones (${roomStats.total})`}
              </FormHelperText>
            </FormControl>

            {/* Selector de Habitación (solo para scope 'specific') */}
            {form.scope === 'specific' && (
              <FormControl fullWidth>
                <InputLabel>Habitación *</InputLabel>
                <Select
                  value={form.roomId}
                  label="Habitación *"
                  onChange={e => {
                    setForm(prev => ({ ...prev, roomId: e.target.value }));
                    const today = dayjs();
                    const tomorrow = today.add(1, 'day');
                    setRange([today, tomorrow]);
                  }}
                  required
                  disabled={loading}
                >
                  <MenuItem value="">Seleccione una habitación</MenuItem>
                  {rooms.map(r => (
                    <MenuItem key={r._id} value={r._id}>
                      {r.name} — {r.type} ({r.lugar}) - Total: {r.totalUnits}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Typography variant="subtitle2">
              Fechas: {form.startDate} → {form.endDate || 'Seleccione fin'}
            </Typography>

            <LocalizationProvider 
              dateAdapter={AdapterDayjs}
              adapterLocale="es"
            >
              <DateCalendar
                value={range[0]}
                onChange={handleRangeSelect}
                minDate={dayjs()}
                reduceAnimations={true}
                slots={{
                  day: (props) => {
                    // Solo validar fechas bloqueadas para scope 'specific' con roomId
                    const blocked = form.scope === 'specific' && form.roomId 
                      ? isDayBlocked(props.day, form.roomId)
                      : false;
                    const inRange = isInRange(props.day, range[0], range[1]);
                    const isStartOrEndDay = isStartOrEnd(props.day, range[0], range[1]);

                    return (
                      <PickersDay
                        {...props}
                        disabled={blocked}
                        sx={{
                          ...(blocked && {
                            bgcolor: 'error.main',
                            color: 'white',
                            '&:hover': {
                              bgcolor: 'error.dark',
                            }
                          }),
                          ...(inRange && {
                            bgcolor: 'primary.light',
                            color: 'white',
                          }),
                          ...(isStartOrEndDay && {
                            bgcolor: 'primary.main',
                            fontWeight: 'bold',
                          }),
                        }}
                      />
                    );
                  },
                }}
              />
            </LocalizationProvider>

            <FormControl fullWidth>
              <InputLabel>Tipo de bloqueo</InputLabel>
              <Select
                value={form.blockType}
                label="Tipo de bloqueo"
                onChange={e => setForm(prev => ({ ...prev, blockType: e.target.value }))}
                disabled={loading}
              >
                {BLOCK_TYPES.map(t => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField 
              label="Motivo" 
              value={form.reason} 
              onChange={e => setForm(prev => ({ ...prev, reason: e.target.value }))}
              multiline
              rows={2}
              disabled={loading}
              placeholder="Descripción del motivo del bloqueo..."
            />

            <Stack direction="row" alignItems="center" spacing={2}>
              <Switch 
                checked={form.scope === 'specific' ? form.blockAll : true}
                onChange={e => setForm(prev => ({ ...prev, blockAll: e.target.checked }))} 
                disabled={loading || form.scope !== 'specific'}
              />
              <Typography variant="body2">
                {form.scope === 'specific' 
                  ? 'Bloquear todas las unidades de esta habitación'
                  : 'Bloquear todas las unidades de cada habitación afectada'}
              </Typography>
            </Stack>

            {form.scope === 'specific' && !form.blockAll && (
              <TextField 
                label="Cantidad a bloquear por habitación" 
                type="number" 
                inputProps={{ min: 1 }} 
                value={form.quantityBlocked} 
                onChange={e => setForm(prev => ({ ...prev, quantityBlocked: Number(e.target.value) }))}
                disabled={loading}
                helperText={
                  form.scope === 'specific' 
                    ? 'Cantidad de unidades a bloquear en esta habitación'
                    : 'Cantidad de unidades a bloquear en cada habitación afectada'
                }
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} disabled={loading}>
              Cancelar
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={loading || !form.endDate || (form.scope === 'specific' && !form.roomId)}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Paper>
  );
}